import React, { useState, useEffect, useCallback } from 'react';
import { Building2 } from 'lucide-react';
import { colors, API } from '../utils/theme';
import {
  PageHeader, SearchBar, Table, Th, Td, TrHover, ActionButtons,
  Modal, FormField, Input, TextArea, Select, Button, LoadingSpinner,
  ConfirmDialog, EmptyState,
} from '../components/UI';
import useSort from '../hooks/useSort';

const SENHA_TIPOS = [
  { value: 'ABERTO', label: 'Sem senha (aberto)' },
  { value: 'CPF_4', label: 'CPF - 4 primeiros dígitos' },
  { value: 'CPF_5', label: 'CPF - 5 primeiros dígitos' },
  { value: 'CPF_6', label: 'CPF - 6 primeiros dígitos' },
  { value: 'CPF_4_CEP_5', label: 'CPF 4 dígitos + CEP 5 dígitos' },
  { value: 'CUSTOM', label: 'Senha personalizada' },
];

const emptyForm = { nome: '', senha_pdf: '', senha_pdf_tipo: 'ABERTO', contato_negociacao: '', observacoes: '' };

export default function BancosPage() {
  const [bancos, setBancos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(() => {
    fetch(`${API}/bancos`).then(r => r.json()).then(setBancos).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = bancos.filter(b =>
    !search || b.nome.toLowerCase().includes(search.toLowerCase())
  );
  const { sorted, sortKey, sortDir, toggleSort } = useSort(filtered, 'nome', 'asc');
  const sortState = { sortKey, sortDir };

  const openCreate = () => { setEditId(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit = (b) => {
    setEditId(b.id);
    setForm({ nome: b.nome || '', senha_pdf: b.senha_pdf || '', senha_pdf_tipo: b.senha_pdf_tipo || 'ABERTO', contato_negociacao: b.contato_negociacao || '', observacoes: b.observacoes || '' });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.nome.trim()) return alert('Nome é obrigatório');
    setSaving(true);
    try {
      const method = editId ? 'PUT' : 'POST';
      const url = editId ? `${API}/bancos/${editId}` : `${API}/bancos`;
      await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      setModalOpen(false);
      load();
    } catch (e) { alert('Erro ao salvar: ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try {
      await fetch(`${API}/bancos/${deleteId}`, { method: 'DELETE' });
      setDeleteId(null);
      load();
    } catch (e) { alert('Erro ao excluir: ' + e.message); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader title="Bancos" subtitle={`${filtered.length} bancos cadastrados`}
        onAdd={openCreate} addLabel="Criar novo Banco" />

      <SearchBar value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar banco..." />

      {filtered.length === 0 ? (
        <EmptyState message="Nenhum banco encontrado" icon={Building2} />
      ) : (
        <Table>
          <thead><tr>
            <Th sortKey="id" sortState={sortState} onSort={toggleSort}>ID</Th>
            <Th sortKey="nome" sortState={sortState} onSort={toggleSort}>Nome</Th>
            <Th>Tipo Senha PDF</Th>
            <Th>Contato Negociação</Th>
            <Th>Observações</Th>
            <Th align="center">Ações</Th>
          </tr></thead>
          <tbody>
            {sorted.map(b => (
              <TrHover key={b.id}>
                <Td style={{ color: colors.textDim }}>{b.id}</Td>
                <Td style={{ fontWeight: 600 }}>{b.nome}</Td>
                <Td style={{ color: colors.textMuted }}>
                  {(SENHA_TIPOS.find(t => t.value === b.senha_pdf_tipo) || SENHA_TIPOS[0]).label}
                  {b.senha_pdf_tipo === 'CUSTOM' && b.senha_pdf ? ` (${b.senha_pdf})` : ''}
                </Td>
                <Td style={{ color: colors.textMuted }}>{b.contato_negociacao || '-'}</Td>
                <Td style={{ color: colors.textMuted, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {b.observacoes || '-'}
                </Td>
                <Td>
                  <ActionButtons onEdit={() => openEdit(b)} onDelete={() => setDeleteId(b.id)} />
                </Td>
              </TrHover>
            ))}
          </tbody>
        </Table>
      )}

      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)} title={editId ? 'Editar Banco' : 'Criar novo Banco'}>
        <FormField label="Nome do Banco" required>
          <Input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} placeholder="Ex: Nubank, Itaú, Bradesco..." />
        </FormField>
        <FormField label="Tipo de Senha do PDF">
          <Select value={form.senha_pdf_tipo} onChange={e => setForm(f => ({ ...f, senha_pdf_tipo: e.target.value }))}>
            {SENHA_TIPOS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </Select>
        </FormField>
        {form.senha_pdf_tipo === 'CPF_4_CEP_5' && (
          <div style={{ background: '#1e3a1a', border: '1px solid #4ade80', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: '#86efac' }}>
            ✅ Derivação automática: 4 primeiros dígitos do CPF + 5 primeiros do CEP do titular.<br/>
            Cadastre o CPF e CEP de cada titular na tela de Titulares para funcionar.
          </div>
        )}
        {form.senha_pdf_tipo === 'CUSTOM' && (
          <FormField label="Senha personalizada">
            <Input value={form.senha_pdf} onChange={e => setForm(f => ({ ...f, senha_pdf: e.target.value }))} placeholder="Senha fixa do PDF" />
          </FormField>
        )}
        <FormField label="Contato para Negociação">
          <Input value={form.contato_negociacao} onChange={e => setForm(f => ({ ...f, contato_negociacao: e.target.value }))} placeholder="Telefone, e-mail..." />
        </FormField>
        <FormField label="Observações">
          <TextArea value={form.observacoes} onChange={e => setForm(f => ({ ...f, observacoes: e.target.value }))} placeholder="Observações gerais..." />
        </FormField>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <Button variant="outline" color={colors.textMuted} onClick={() => setModalOpen(false)}>Cancelar</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Salvando...' : editId ? 'Salvar Alterações' : 'Criar Banco'}</Button>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete}
        title="Excluir Banco" message="Tem certeza que deseja excluir este banco?"
      />
    </div>
  );
}
